/*
  Kiosk-mode Shop-Floor UI for operators. Big fingers, big buttons.
  Renders OUTSIDE the normal <AppLayout>, at route /shopfloor.
  Operator picks a machine → sees today's job cards → setup/run/pause/complete
  with just 4 large touch-friendly buttons.
*/
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Play, Pause, CheckCircle2, Settings, ChevronLeft, Cpu, LogOut, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { fmtNum } from "@/lib/format";

const REASONS = ["breakdown", "no_material", "no_operator", "setup", "quality", "planned_maint", "other"];

function Big({ children, onClick, disabled, className = "", "data-testid": tid }) {
  return (
    <button data-testid={tid} onClick={onClick} disabled={disabled}
      className={`min-h-24 text-lg font-medium rounded-none border-2 flex flex-col items-center justify-center gap-2 transition ${disabled ? "opacity-30 cursor-not-allowed" : "hover:scale-[1.02]"} ${className}`}>
      {children}
    </button>
  );
}

export default function ShopFloor() {
  const [step, setStep] = useState("machine");   // machine → cards → running
  const [machines, setMachines] = useState([]);
  const [machine, setMachine] = useState(null);
  const [cards, setCards] = useState([]);
  const [active, setActive] = useState(null);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [qtyGood, setQtyGood] = useState(0);
  const [qtyRej, setQtyRej] = useState(0);
  const [rejReason, setRejReason] = useState("");
  const [pauseReason, setPauseReason] = useState("breakdown");

  useEffect(() => {
    if (step !== "machine") return;
    api.get("/mfg/machines", { params: { active_only: true } }).then((r) => setMachines(r.data)).catch(() => {});
  }, [step]);

  const loadCards = async (m) => {
    setMachine(m); setStep("cards");
    const r = await api.get("/production/job-cards", { params: { machine_id: m.id } });
    setCards(r.data.filter((c) => c.status !== "completed"));
  };
  const refreshCards = async () => {
    if (!machine) return;
    const r = await api.get("/production/job-cards", { params: { machine_id: machine.id } });
    setCards(r.data.filter((c) => c.status !== "completed"));
  };
  const startSetup = async (jc) => { try { await api.post(`/production/job-cards/${jc.id}/setup`); toast.success("Setup started"); refreshCards(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const startRun = async (jc) => { try { await api.post(`/production/job-cards/${jc.id}/run`); toast.success("Running"); refreshCards(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const pauseRun = async () => { try { await api.post(`/production/job-cards/${active.id}/pause`, { reason: pauseReason }); toast.success("Paused"); setPauseOpen(false); refreshCards(); setActive(null); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const complete = async () => {
    try { await api.post(`/production/job-cards/${active.id}/complete`, { qty_good: Number(qtyGood), qty_rejected: Number(qtyRej), rejection_reason: rejReason });
      toast.success("Completed"); setCompleteOpen(false); setActive(null); setQtyGood(0); setQtyRej(0); setRejReason(""); refreshCards();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="fixed inset-0 bg-neutral-950 text-white font-sans" data-testid="shopfloor-kiosk">
      {/* Header */}
      <div className="h-16 border-b border-white/10 flex items-center px-6 gap-4 bg-black">
        {step !== "machine" && <button onClick={() => { setStep("machine"); setMachine(null); setActive(null); }} className="text-white/60 hover:text-white" data-testid="sf-back"><ChevronLeft className="w-6 h-6" /></button>}
        <div className="flex-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// Shop-Floor Kiosk</div>
          <div className="font-medium">{machine ? `${machine.code} · ${machine.name}` : "Select your machine"}</div>
        </div>
        <a href="/app/dashboard" className="text-white/40 hover:text-white text-sm gap-1 flex items-center" data-testid="sf-exit"><LogOut className="w-4 h-4" /> Exit</a>
      </div>

      {/* Body */}
      <div className="p-6 h-[calc(100vh-4rem)] overflow-y-auto">
        {/* Step 1: Pick machine */}
        {step === "machine" && (
          <div>
            <div className="text-white/70 mb-4">Tap your work-center to see today's job cards.</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {machines.length === 0 && <div className="col-span-full text-white/40 text-center py-12">No active machines. Ask supervisor to onboard machines.</div>}
              {machines.map((m) => (
                <Big key={m.id} onClick={() => loadCards(m)} data-testid={`sf-machine-${m.code}`}
                     className="bg-white/5 border-white/10 hover:border-accent hover:bg-accent/10 text-white">
                  <Cpu className="w-8 h-8 text-accent" />
                  <div className="font-mono text-xl">{m.code}</div>
                  <div className="text-xs text-white/60">{m.name}</div>
                </Big>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Job cards for machine */}
        {step === "cards" && machine && (
          <div className="space-y-3">
            <div className="text-white/70">Pending jobs on this machine.</div>
            {cards.length === 0 && <div className="text-white/40 text-center py-16 border-2 border-dashed border-white/10">No open job cards. Enjoy the coffee ☕</div>}
            {cards.map((jc) => (
              <div key={jc.id} className="border-2 border-white/10 p-4 flex items-center gap-4" data-testid={`sf-jc-${jc.jc_number}`}>
                <div className="w-24 text-center">
                  <div className="text-3xl font-medium text-accent">{jc.op_seq}</div>
                  <div className="text-[10px] font-mono uppercase text-white/50">op</div>
                </div>
                <div className="flex-1">
                  <div className="text-lg font-medium">{jc.operation_name}</div>
                  <div className="text-xs font-mono text-white/60">{jc.jc_number} · {jc.wo_number}</div>
                  <div className="text-xs text-white/70 mt-1">Plan {fmtNum(jc.qty_planned)} · Done {fmtNum(jc.qty_good)} · Rej {fmtNum(jc.qty_rejected)}</div>
                </div>
                <div className={`text-[10px] font-mono uppercase px-2 py-0.5 border ${jc.status === "running" ? "text-yellow-400 border-yellow-400" : jc.status === "setup" ? "text-blue-400 border-blue-400" : "text-white/40 border-white/20"}`}>{jc.status}</div>
                <div className="flex gap-2">
                  {jc.status === "pending" && (
                    <Big onClick={() => startSetup(jc)} data-testid={`sf-setup-${jc.jc_number}`} className="w-28 bg-blue-500/20 border-blue-500/40 text-white hover:bg-blue-500/40"><Settings className="w-6 h-6" /><span className="text-sm">Setup</span></Big>
                  )}
                  {(jc.status === "pending" || jc.status === "setup" || jc.status === "paused") && (
                    <Big onClick={() => startRun(jc)} data-testid={`sf-start-${jc.jc_number}`} className="w-28 bg-emerald-500/20 border-emerald-500/40 text-white hover:bg-emerald-500/40"><Play className="w-8 h-8" /><span className="text-sm">Start</span></Big>
                  )}
                  {jc.status === "running" && (
                    <>
                      <Big onClick={() => { setActive(jc); setPauseOpen(true); }} data-testid={`sf-pause-${jc.jc_number}`} className="w-28 bg-orange-500/20 border-orange-500/40 text-white hover:bg-orange-500/40"><Pause className="w-6 h-6" /><span className="text-sm">Pause</span></Big>
                      <Big onClick={() => { setActive(jc); setQtyGood(jc.qty_planned); setCompleteOpen(true); }} data-testid={`sf-done-${jc.jc_number}`} className="w-28 bg-emerald-500/30 border-emerald-500/60 text-white hover:bg-emerald-500/50"><CheckCircle2 className="w-8 h-8" /><span className="text-sm">Done</span></Big>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Complete dialog */}
      {completeOpen && active && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-6 z-50" data-testid="sf-complete-modal">
          <div className="bg-neutral-900 border-2 border-white/10 p-6 w-full max-w-md space-y-4">
            <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Complete Operation</div>
            <div className="font-medium text-lg">{active.operation_name}</div>
            <div>
              <label className="text-xs text-white/60">Good qty</label>
              <Input type="number" value={qtyGood} onChange={(e) => setQtyGood(e.target.value)} className="rounded-sm bg-white/5 border-white/20 h-14 text-2xl text-center tabular-nums text-white" data-testid="sf-qty-good" />
            </div>
            <div>
              <label className="text-xs text-white/60 flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-red-400" /> Rejected qty</label>
              <Input type="number" value={qtyRej} onChange={(e) => setQtyRej(e.target.value)} className="rounded-sm bg-white/5 border-white/20 h-14 text-2xl text-center tabular-nums text-white" data-testid="sf-qty-rej" />
            </div>
            {qtyRej > 0 && (
              <div>
                <label className="text-xs text-white/60">Rejection reason</label>
                <Input value={rejReason} onChange={(e) => setRejReason(e.target.value)} className="rounded-sm bg-white/5 border-white/20 text-white" placeholder="Dimension out of tolerance…" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <Button onClick={() => setCompleteOpen(false)} variant="ghost" className="rounded-sm h-14 text-white/70">Cancel</Button>
              <Button onClick={complete} className="rounded-sm h-14 bg-emerald-500 hover:bg-emerald-600 text-white text-lg" data-testid="sf-confirm-complete">Confirm</Button>
            </div>
          </div>
        </div>
      )}

      {/* Pause dialog */}
      {pauseOpen && active && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-6 z-50" data-testid="sf-pause-modal">
          <div className="bg-neutral-900 border-2 border-white/10 p-6 w-full max-w-md space-y-4">
            <div className="text-[10px] font-mono uppercase tracking-widest text-orange-400">Pause / Downtime</div>
            <div className="font-medium text-lg">{active.operation_name}</div>
            <div className="grid grid-cols-2 gap-2">
              {REASONS.map((r) => (
                <button key={r} onClick={() => setPauseReason(r)} data-testid={`sf-reason-${r}`}
                        className={`p-3 border-2 text-sm ${pauseReason === r ? "border-orange-500 bg-orange-500/20" : "border-white/10 text-white/70"}`}>
                  {r.replace(/_/g, " ")}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button onClick={() => setPauseOpen(false)} variant="ghost" className="rounded-sm h-14 text-white/70">Cancel</Button>
              <Button onClick={pauseRun} className="rounded-sm h-14 bg-orange-500 hover:bg-orange-600 text-white text-lg" data-testid="sf-confirm-pause">Pause</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
